import React, { useContext, useMemo } from 'react';

import { Box, Flex, List, ListItem } from '@opendoor/bricks/core';
import { IconName } from '@opendoor/bricks/core/Icon';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { colors } from '@opendoor/bricks/theme/eero';

import { useBrowserCompatibilityData } from 'shared/helpers/globals';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import {
  getHubBuyingRoute,
  getHubBuyingSearchRoute,
  getHubContactRoute,
  getHubMoreRoute,
  getHubSellingRoute,
} from 'shared/helpers/routes';

import { getPartnershipBlockerModalType } from 'buying/helpers/partnerships';

import { DesktopMoreContent } from 'platform/components/layout/More';
import { NavTab } from 'platform/components/layout/NavTab';
import { OpendoorLogo } from 'platform/components/OpendoorLogo';

// Do not change tab names. They are used in our metrics.
export const SELLING_TAB_NAME = 'sell';
export const BUYING_TAB_NAME = 'buy';
export const MORE_TAB_NAME = 'more';
export const CONTACT_TAB_NAME = 'contact';

export type TabName =
  | typeof SELLING_TAB_NAME
  | typeof BUYING_TAB_NAME
  | typeof MORE_TAB_NAME
  | typeof CONTACT_TAB_NAME;
export const ActiveTabNameContext = React.createContext<TabName>(SELLING_TAB_NAME);

/**
 * On mobile only, we have a More tab.
 */
export const MobilePrimaryNavBar: React.FC = () => {
  const activeTabName = useContext(ActiveTabNameContext);
  const { blockBuyerContent, partnershipReferralData } = useQualificationContext();
  const showBuyTabForPartnership = !!(
    partnershipReferralData && getPartnershipBlockerModalType(partnershipReferralData)
  );

  const tabs = useTabs(blockBuyerContent, showBuyTabForPartnership);
  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();
  return (
    <Box
      as="nav"
      data-testid="mobile-primary-nav"
      borderTop={`1px solid ${colors.neutrals30}`}
      bgColor="neutrals0"
    >
      <List variant="text" display="flex" justifyContent="space-evenly" width="100%">
        {tabs.map((tab) => (
          <ListItem key={tab.name}>
            <NavTab
              name={tab.name}
              iconName={tab.iconName}
              to={tab.href}
              isActive={activeTabName === tab.name}
              css={{
                '&:hover': isOpendoorAppEvoWebView
                  ? {
                      color: activeTabName === tab.name ? colors.neutrals100 : colors.neutrals70,
                      backgroundColor: 'transparent',
                    }
                  : {},
              }}
              aria-label={tab.displayName}
            >
              {tab.displayName}
            </NavTab>
          </ListItem>
        ))}
        <ListItem>
          <NavTab
            name={MORE_TAB_NAME}
            iconName="avatar"
            to={getHubMoreRoute()}
            isActive={activeTabName === MORE_TAB_NAME}
            aria-label="More"
          >
            Account
          </NavTab>
        </ListItem>
      </List>
    </Box>
  );
};

/**
 * On desktop only, instead of a More tab, the More content is splat out directly on the nav.
 */
export const DesktopPrimaryNavBar: React.FC = () => {
  const activeTabName = useContext(ActiveTabNameContext);
  const { blockBuyerContent, partnershipReferralData } = useQualificationContext();

  const showBuyTabForPartnership = !!(
    partnershipReferralData && getPartnershipBlockerModalType(partnershipReferralData)
  );
  const { isTablet } = useDevice();

  const tabs = useTabs(blockBuyerContent, showBuyTabForPartnership);
  return (
    <Flex
      as="nav"
      aria-label="Global"
      width={[null, null, '107px', '200px']}
      height="100%"
      borderRight={`1px solid ${colors.neutrals30}`}
      bgColor="neutrals0"
      data-testid="desktop-primary-nav"
      flexDir="column"
      justifyContent="space-between"
      px={5}
    >
      <Box>
        <OpendoorNavLogo />
        <Flex as="nav" aria-label="App" flexDirection="column">
          <List variant="text">
            {tabs.map((tab) => (
              <ListItem key={tab.name}>
                <NavTab
                  width="auto"
                  name={tab.name}
                  iconName={tab.iconName}
                  to={tab.href}
                  isActive={activeTabName === tab.name}
                  aria-label={tab.displayName}
                >
                  {tab.displayName}
                </NavTab>
              </ListItem>
            ))}
          </List>
        </Flex>
      </Box>
      <Box
        borderTop={`1px solid ${colors.neutrals30}`}
        py={7}
        alignSelf={isTablet ? 'center' : undefined}
      >
        <DesktopMoreContent />
      </Box>
    </Flex>
  );
};

const OpendoorNavLogo = () => {
  const { isDesktop } = useDevice();

  return (
    <Flex alignItems="center" justifyContent="center" pt={7} pb={9}>
      {isDesktop ? (
        <OpendoorLogo variant="primary" width="151px" height="32px" />
      ) : (
        <OpendoorLogo variant="secondary" width="32px" height="45px" />
      )}
    </Flex>
  );
};

interface PrimaryNavBarTab {
  /** Name used for metrics only. */
  name: TabName;
  iconName: IconName;
  displayName: string;
  href: string;
}

const useTabs = (
  blockBuyerContent: boolean,
  showBuyTabForPartnership: boolean,
): PrimaryNavBarTab[] => {
  return useMemo(() => {
    const tabs: PrimaryNavBarTab[] = [];
    tabs.push({
      name: SELLING_TAB_NAME,
      iconName: 'sign',
      displayName: 'Sell',
      href: getHubSellingRoute(),
    });
    if (!blockBuyerContent || showBuyTabForPartnership) {
      tabs.push({
        name: BUYING_TAB_NAME,
        iconName: 'search',
        displayName: 'Buy',
        href: showBuyTabForPartnership ? getHubBuyingSearchRoute() : getHubBuyingRoute(),
      });
    }
    tabs.push({
      name: CONTACT_TAB_NAME,
      iconName: 'chat-inactive',
      displayName: 'Contact',
      href: getHubContactRoute(),
    });
    return tabs;
  }, [blockBuyerContent, showBuyTabForPartnership]);
};
